import React, { useState } from 'react';
import { Check, ArrowRight, Star } from 'lucide-react';
import { bannerUrl } from '../../lib/api';
import { readUserParams, appendParamsToUrl, getSessionId } from '../../lib/userParams';
import OfferQualifierDialog from './OfferQualifier';

export default function OfferAd({ offer, index = 0 }) {
  const [qOpen, setQOpen] = useState(false);
  if (!offer) return null;
  const userParams = readUserParams();
  const sid = getSessionId();
  const hasQualifier = !!(offer.qualifier && offer.qualifier.enabled);
  // Anchor href is the branded short URL (same domain), with the visitor's tracked params
  // merged in. Hover, status bar and address bar all display our own theloanalliance.com URL.
  const baseUrl = offer.cta_url ? appendParamsToUrl(offer.cta_url, { ...userParams, sid }) : '';
  const testSlug = (offer.id || '').slice(0, 8);

  const openTarget = (extraParams = {}) => {
    if (!baseUrl) return;
    const finalUrl = Object.keys(extraParams).length > 0 ? appendParamsToUrl(baseUrl, extraParams) : baseUrl;
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClick = (e) => {
    if (!baseUrl) { e.preventDefault(); return; }
    if (hasQualifier) {
      e.preventDefault();
      setQOpen(true);
    }
    // else: let the anchor navigate normally (target=_blank)
  };

  const onQualifierConfirm = (answer) => {
    setQOpen(false);
    const fieldName = offer.qualifier?.field_name || 'answer';
    openTarget({ [fieldName]: answer.value });
  };

  const anchorProps = baseUrl
    ? { href: baseUrl, target: '_blank', rel: 'noopener noreferrer', onClick: handleClick }
    : { href: '#', onClick: (e) => e.preventDefault(), 'aria-disabled': true };

  return (
    <div className="group relative rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-emerald-300 hover:shadow-xl transition-all duration-300"
         data-testid={`offer-card-${testSlug}`}>
      {offer.badge && (
        <div className="absolute top-4 left-4 z-10 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold tracking-widest uppercase shadow">
          {offer.badge}
        </div>
      )}
      <div className="grid md:grid-cols-5 gap-0">
        {/* Banner - left side, clickable */}
        <a
          {...anchorProps}
          data-testid={`offer-banner-${testSlug}`}
          aria-label={`View offer: ${offer.title}`}
          className={`relative md:col-span-2 h-56 md:h-full min-h-[240px] overflow-hidden bg-gradient-to-br from-emerald-50 to-teal-50 block ${baseUrl ? 'cursor-pointer' : 'cursor-default'}`}
        >
          {offer.banner_url ? (
            <>
              <img
                src={bannerUrl(offer.banner_url)}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-emerald-50/40" />
              <img
                src={bannerUrl(offer.banner_url)}
                alt={offer.title}
                className="relative w-full h-full object-contain object-center p-4 transition-transform duration-700 group-hover:scale-105"
              />
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-emerald-600 font-serif-display text-xl px-4 text-center">
              {offer.title}
            </div>
          )}
        </a>

        {/* Content - right side */}
        <div className="md:col-span-3 p-6 lg:p-7 flex flex-col">
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-serif-display text-2xl lg:text-3xl font-bold text-slate-900">{offer.title}</h3>
            <div className="hidden md:flex items-center gap-1 text-sm text-slate-700 shrink-0">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="font-semibold">{(4.4 + ((index % 5) * 0.1)).toFixed(1)}</span>
            </div>
          </div>
          <p className="mt-2 text-slate-600 leading-relaxed text-sm lg:text-base">{offer.description}</p>
          {offer.features && offer.features.length > 0 && (
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 flex-1">
              {offer.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <a
              {...anchorProps}
              data-testid={`offer-cta-${testSlug}`}
              className="inline-flex items-center gap-1.5 h-11 px-6 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm transition-colors disabled:opacity-50 group/btn no-underline"
            >
              {offer.cta_label || 'Learn More'}
              <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" />
            </a>
            {baseUrl && (
              <span className="text-xs text-slate-400">Redirects to partner site</span>
            )}
          </div>
        </div>
      </div>

      {hasQualifier && (
        <OfferQualifierDialog
          open={qOpen}
          offer={offer}
          onOpenChange={setQOpen}
          onConfirm={onQualifierConfirm}
        />
      )}
    </div>
  );
}
